import { WizardTranslations } from "./wizard-pt-br";

export const WIZARD_EN_US: WizardTranslations = {
	common: {
		pointsOfSale: "Points of sale",
		saved: "Save",
		cancel: "Cancel",
		typeHere: "Type here",
		close: "Close",
	},
	welcome: {
		title: "Welcome, <placeName>{{placeName}}</placeName>!",
		subtitle: "Choose how you want to configure your operation.",
		skipButton: "I don't want to configure now",
		placeIdNotFound: "Place ID not found",
		features: {
			preConfiguredSteps: "Steps with pre-configured suggestions",
			guidedStepsWithAI: "Step-by-step guided with AI help",
			advancedConfigurations: "Advanced configurations and customizations",
			complexFeatures: "More complex features",
		},
		guidedMode: {
			title: "Guided creation (simplified mode)",
			subtitle: "We guide you step by step",
			buttonText: "Access simplified mode",
			features: {
				basicConfigurations: "More basic configurations",
				idealForSimpleOperations: "Ideal for simpler and smaller operations",
			},
		},
		advancedMode: {
			title: "Advanced creation",
			subtitle: "More options and configuration details",
			buttonText: "Access advanced mode",
			features: {
				directConfiguration: "Direct configuration in Dashboard modules",
				idealForComplexOperations: "Ideal for large and complex operations",
			},
		},
	},
	container: {
		stepIndexTitle: "Configure your place",
		buttons: {
			back: "Back",
			close: "Close",
			next: "Next",
			configureLater: "Configure later",
			finishConfiguration: "Finish configuration",
			backToStep: "Back to Step {{step}}",
		},
		finishConfirmation: {
			title: "Complete operation settings",
			descriptionPart1: "You are about to complete the configuration of this operation.",
			descriptionPart2:
				"A final summary will be generated and will be available for consultation at any time. Afterwards, you will be able to review and edit when necessary.",
			cancelButton: "Cancel",
			confirmButton: "Finish configuration",
			successMessage: "Settings saved successfully!",
			continueToNextStep: {
				title: "Complete and continue to next step",
				descriptionPart1: "You are about to complete this step.",
				descriptionPart2:
					"When you continue, the settings will be saved and you will proceed to the next step.",
				confirmButton: "Save and continue",
			},
		},
		closeModalConfirmation: {
			title: "Exit configuration without saving?",
			description:
				"By closing now, the information configured so far will not be saved and you will lose progress. Do you want to continue?",
			cancelButton: "Continue configuring",
			confirmButton: "Exit without saving",
			fromSecondStep: {
				title: "Exit simplified configuration?",
				descriptionIntro:
					"This assistant helps you configure your operation in a simplified way. If you leave now:",
				bullet1: "You will not be able to return here to continue the configuration",
				bullet2: "The information from this step will be discarded",
				bullet3: "Completed steps will remain saved",
				descriptionOutro:
					"If you leave now, further configuration will need to be done directly in the advanced Dashboard screens.",
				cancelButton: "Continue configuring",
				confirmButton: "Exit simplified configuration",
			},
		},
		steps: {
			basicInformation: {
				title: "Basic information",
				subtitle: "Configure the operation type",
				operationType: {
					title: "How will customers <highlight>purchase</highlight>?",
					subtitle: "Choose one or more formats that will apply to this operation.",
					alert: {
						message:
							"These options are already selected based on your operation's contract.",
						description: "You can keep them as they are or adjust them if you'd like.",
					},
					subStepTitle: "Operation type",
				},
				functionalities: {
					title:
						"What will be the <highlight>functionalities</highlight> of this operation?",
					subtitle:
						"Functionalities are options with resources that allow configuring the operation in a personalized way.",
					subStepTitle: "Functionalities",
					searchFunctionalities: "Search by functionality",
					placeholderSearchFunctionalities: "Enter here the functionality",
					activeLabel: "active",
					loadingText: "Loading functionalities...",
					emptySearchResults: "No functionality found",
				},
				review: {
					title:
						"We have already understood the <highlight>initial configurations</highlight> of the operation!",
					subtitle: "According to your answers, the system will configure:",
					subStepTitle: "Review",
					reviewTitle: "Review the answers",
					reviewSubtitle: "You can edit them now, before completing the step.",
					editStep: "Edit step",
					operationTypeLabel: "Operation Type",
					functionalitiesLabel: "Functionalities",
					notInformed: "Not informed",
					selectedCount: "{{count}} selected",
					viewFunctionalities: "View functionalities",
				},
			},
			pointsOfSale: {
				pdvCentralName: "Central point of sale",
				title: "Points of sale",
				subtitle: "Configure sales locations",
				pointsOfSale: {
					subStepTitle: "Points of sale",
					title: "What will be the points of sale for this operation?",
					subtitle:
						"Name only the points of sale that will be the locations where the Zig machines will be placed.",
					createButton: "Create point of sale",
					defaultPdvName: "New point of sale",
					totalCount: "Total points of sale: <strong> {{ count}} </strong>",
					aiTutorial: "Learn how to use AI to create points of sale",
					emptyTitle: "No points of sale yet",
					emptySubtitle: "Create your first point of sale to get started.",
					tour: {
						step1: {
							title:
								"El punto de venta es donde ocurren las ventas de tu operación a través de las máquinas Zig.",
							content:
								"Si tu operación tiene solo 1 (uno) punto de venta, simplemente continúa. Si necesitas editar o crear nuevos puntos, puedes hacerlo ahora.",
						},
						step2: {
							title: "Puedes crear los puntos de venta con ayuda de la IA",
							content:
								"Si quieres utilizar la IA, describe los nombres y cantidades deseadas, y serán generados automáticamente.",
						},
						step3: {
							title:
								"Want to review something? Use the side menu to go back to completed steps.",
							content:
								"Just click on a completed step to review the settings and return to the current step to continue where you left off.",
						},
					},
					aiTutorialModal: {
						step1: {
							title: "Click on the <highlight>AI Assistant</highlight>",
							description:
								"Open the Assistant to start automatically creating your points of sale.",
						},
						step2: {
							title:
								"Describe the names and quantities of the <highlight>points of sale</highlight>",
							description:
								"Inform the names and quantities of the locations where sales with the Zig machines will take place. (Ex.: 1 Main Bar, 2 Premium Tracks, 1 Kitchen Area)",
							note: "Be thorough with your descriptions!",
						},
						step3: {
							title: "Confirm and <highlight>generate the points of sale</highlight>",
							description:
								"Review the summary that the AI shows you and click 'Generate points of sale' to create them automatically.",
						},
						buttons: {
							understand: "Got it",
						},
					},
					aiAssistant: {
						panelTitle: "Zig AI Assistant",
						triggerBubbleTitle: "I'm your Zig AI Assistant.",
						triggerBubbleSubtitle: "I'll help you create your points of sale",
						inputPlaceholder: "Describe the points of sale or ask something",
						generateButton: "Generate points of sale",
						initialMessage:
							"👋 Hi! I'm your AI Assistant.\nI'm here to help you create the points of sale for your operation.\n\n📋 Just tell me what you need (names and quantities).\nI'll review everything with you and, with your confirmation, generate them automatically.\n\n💬 Let's get started? Which points of sale would you like to create?",
						summaryIntro: "Great! Here is a summary of what you described:",
						summaryConfirmation:
							"If everything looks correct, just click below to generate the points of sale.",
						loadingMessage: "Creating...",
						successMessage: "🎉 Done! The points of sale have been created successfully!",
						successFollowUp:
							"If you want to add more points of sale, just send me the names and quantities again.",
						onlyCreateError:
							"😅 Sorry, I can only create new points of sale here. If you need to edit or remove an existing one, you can do that directly on the screen.",
					},
				},
				sectors: {
					subStepTitle: "Sectors",
					title:
						"Do you want to organize your points of sale into <highlight>sectors</highlight>?",
					subtitle:
						"Define whether you prefer to structure your points of sale into sectors or keep each one separate.",
					options: {
						withoutSectors: {
							title: "Keep points of sale separate (no sectors)",
							subtitle:
								"Points of sale will remain independent, without grouping by sector.",
						},
						withSectors: {
							title: "Organize points of sale by sectors",
							subtitle: "Points of sale will be grouped within the sectors you define.",
						},
					},
					placeholder: {
						message: "The sectors feature will be implemented soon.",
					},
					creation: {
						title: "Group all points of sale into sectors",
						subtitle: "All points of sale must belong to a sector.",
						createButton: "Create new Sector",
						emptyTitle: "Still nothing here",
						emptySubtitle: "Group points of sale into sectors",
						unlinkedCount: "{{count}} Points of sale to be grouped",
						tour: {
							title: "Create the first sector",
							content:
								"Click the 'Create new sector' button to start organizing your points of sale.",
							understood: "Got it",
						},
						deleteConfirmTitle: "Delete sector?",
						deleteConfirmSubtitle:
							"This sector will be removed. Its points of sale can be assigned to another sector.",
					},
					creationDrawer: {
						createTitle: "Create new Sector",
						editTitle: "Edit Sector",
						nameLabel: "Name",
						namePlaceholder: "Enter the sector name",
						nameRequired: "Required field",
						pointsOfSaleLabel: "Points of sale in the sector",
						pointsOfSalePlaceholder: "Select the points of sale",
						pointsOfSaleRequired: "Required field",
						createButton: "Create new Sector",
						saveButton: "Save",
					},
				},
				review: {
					subStepTitle: "Review",
					title: "Step 2 complete! <highlight>Points of sale</highlight> configured!",
					subtitle:
						"The points of sale you created are ready for products and employees.",
					reviewTitle: "Review the answers",
					reviewSubtitle: "You can edit them now or at the end of the flow.",
					reviewAlertTitle: "Review this step carefully.",
					reviewAlert:
						"Once you proceed, these settings can no longer be edited here and can only be adjusted directly in the Dashboard modules.",
					editStep: "Edit",
					editPointsOfSale: "Points of sale",
					editSectors: "Sectors",
					pointsOfSaleLabel: "Points of sale",
					notInformed: "Not informed",
				},
				pdvCreateNewPdvDrawer: {
					title: "This point of sale name already exists",
					subtitle:
						"A point of sale with this name already exists. To continue, choose a different name to avoid duplication.",
					newPdvDrawerTitle: "Name of the point of sale",
					createButton: "Create point of sale",
					nameRequired: "The point of sale name is required",
				},
			},
			menu: {
				title: "Menu",
				subtitle: "Configure products",
				organization: {
					title:
						"Will <highlight>sales be different</highlight> depending on the point of sale?",
					subtitle:
						"Define whether the points of sale will share the same products and prices or if there will be differences between them.",
					subStepTitle: "Organization",
					yes: {
						title: "YES",
						subtitle: "Each point of sale will have its own products and prices.",
						example:
							"Example: Each point of sale has its own sales with different products.",
					},
					no: {
						title: "NO",
						subtitle:
							"All points of sale will have the same products and the same prices.",
						example: "Example: In any point of sale, the product options are identical.",
					},
					changeFormatModal: {
						title: "Do you want to change the sales format for points of sale?",
						description:
							"You are changing how products are sold between points of sale. This change may impact current settings and may require adjustments according to the new choice. Do you want to continue anyway?",
						cancelButton: "Cancel",
						confirmButton: "Yes, change sales format",
					},
				},
				buildMenus: {
					title: "Build your <highlight>menu</highlight>",
					subtitle: "Import products with photos, spreadsheets or PDFs.",
					subStepTitle: "Build menus",
					templateFileName: "menu_products_template",
					buttons: {
						templateSpreadsheet: "Template spreadsheet",
						createCategory: "Create category",
						addManually: "Add manually",
					},
					tour: {
						locale: {
							back: "Back",
							next: "Next",
							close: "Got it",
						},
						steps: {
							upload: {
								title: "Start by uploading a file with your products.",
								content:
									"You can add a photo, spreadsheet, or PDF with the products you want to include in this menu. The AI will read the file and automatically register the items for you.",
							},
							manual: {
								title: "Or, if you prefer, add products manually.",
								content:
									"If you wish, you can create categories and add products manually without using a file.",
							},
						},
					},
					addProductDrawer: {
						title: "Add product",
						titleEdit: "Edit product",
						labels: {
							name: "Product name",
							value: "Price",
							category: "Product category",
						},
						placeholders: {
							name: "Enter the product name here",
							value: "00.00",
							category: "Select",
						},
						errors: {
							nameRequired: "Product name is required",
							nameDuplicated: "A product with this name already exists",
							valueRequired: "Product price is required",
							categoryRequired: "Category is required",
						},
						buttons: {
							cancel: "Cancel",
							add: "Add product",
							save: "Save",
						},
						createCategoryOption: "Create category",
					},
					createCategoryModal: {
						title: "Create category",
						label: "Category name",
						placeholder: "Enter the category name here",
						errors: {
							nameRequired: "Category name is required",
							nameDuplicated: "A category with this name already exists",
						},
						buttons: {
							cancel: "Cancel",
							create: "Create category",
						},
					},
					removeCategory: {
						title: "Remove category",
						description:
							"By removing this category, all products linked to it will also be removed. Do you want to continue?",
						confirmButton: "Remove category",
					},
					removeProduct: {
						title: "Remove product",
						description:
							"This product will be removed from the menu and will no longer be available for sale at points of sale. Do you want to confirm the removal?",
						confirmButton: "Remove product",
					},
				},
				linkProducts: {
					title: "Link <highlight>products</highlight> to points of sale",
					subtitle:
						"Menus and products will be sold at the point of sale they are linked to",
					subStepTitle: "Link products",
					noProductsLinked: "No products linked!",
					productsCount: "{{count}} products",
					productsCount_one: "{{count}} product",
					linkProductsButton: "Link products",
					viewProductsLink: "View products",
					incompleteModal: {
						title: "Some points of sale are incomplete",
						description:
							"Some points of sale have no menus and products linked. You can link now or continue and edit later.",
						continueButton: "Continue and edit later",
						editButton: "Edit now",
					},
					tour: {
						locale: {
							close: "Got it",
						},
						step: {
							title: "Define what each point of sale will sell.",
							content:
								"Select the menus that this point will have. You can also disable/enable specific products, leaving only what will be sold there.",
						},
					},
					drawer: {
						editTitle: "Select menus for this point of sale",
						editSubtitle:
							"Products from these menus will be linked to this point. You can disable specific products if you wish.",
						viewTitle: "Linked products",
						viewSubtitle:
							"Review the products of this point of sale and edit the links if you wish.",
						selectAllLabel: "Select all products from {{categoryName}}",
						labels: {
							menus: "Menus",
							searchProduct: "Search by product",
							searchCategory: "Search by product category",
						},
						placeholders: {
							menus: "Select",
						},
						buttons: {
							cancel: "Cancel",
							link: "Link products",
							edit: "Edit",
							close: "Close",
						},
						empty: {
							title: "Nothing here yet",
							subtitle: "Link which menus this point of sale will have.",
						},
						noResultsFound: {
							title: "No results found",
							subtitle: "Try searching for another term.",
						},
					},
				},
				review: {
					title: "Review the <highlight>menu settings</highlight>",
					subtitle: "Check if all information is correct before continuing.",
					subStepTitle: "Review",
					pendingTitle: "Step 3: <highlight>pending settings</highlight>",
					pendingSubtitle: "You decided to configure this step later.",
					reviewTitle: "Review your answers",
					reviewSubtitle: "You can edit them now, before completing the step.",
					editStep: "Edit step",
				},
			},
			employees: {
				title: "Employees",
				subtitle: "Configure the operation team",
				rolesAndPermissions: {
					title:
						"Who will work using the <highlight>token machines</highlight> in the operation?",
					subtitle:
						"Define the roles and quantities of who will sell tokens with the machines in the operation.",
					subStepTitle: "Roles and permissions",
					header: "Roles and permissions",
					totalEmployees: "Total employees:",
					createRole: "Create new role",
					roleDrawer: {
						title: "Create role",
						roleNameLabel: "Role name",
						selectStandardRole: "Select an existing standard role",
						selectStandardRolePlaceholder: "Select a role",
						defaultPermissionsTab: "Standard permissions",
						otherPermissionsTab: "Other permissions",
						createButton: "Create role",
						duplicateNameError: "A role with this name already exists",
						editTitle: "Edit role",
						duplicateTitle: "Duplicate role",
						viewTitle: "View permissions",
						editRoleButton: "Edit role",
						confirmButton: "Confirm",
						searchPermissionsPlaceholder: "Search for a permission",
						confirmClose: {
							title: "Discard changes?",
							subtitle:
								"You have unsaved changes. Are you sure you want to close without saving?",
							confirm: "Discard",
							cancel: "Keep editing",
						},
					},
					confirmDelete: {
						title: "Remove role with linked employees",
						subtitle:
							"This role has {{count}} linked employee(s). Do you still want to remove it?",
						confirm: "Remove role",
						cancel: "Cancel",
					},
					tour: {
						locale: {
							back: "Back",
							next: "Next",
							close: "Got it",
						},
						step1: {
							title: "These roles are suggestions for your operation.",
							content:
								"You can edit or remove them as desired. If you don't want a role to exist in the operation, remove it. Otherwise, it will be created even if the quantity is zero.",
						},
						step2: {
							title: "Define how many people will hold each role",
							content:
								"Use the counter to indicate how many people will work in each role. Only roles with a defined quantity will have logins generated automatically.",
						},
						step3: {
							title: "Create custom roles",
							content:
								"If you need a role different from the suggested ones, click Create new role to add specific functions and define custom permissions.",
						},
					},
				},
				linkEmployees: {
					title: "Link employees",
					subtitle: "Associate employees to each point of sale.",
					subStepTitle: "Link employees",
					tour: {
						locale: {
							back: "Back",
							next: "Next",
							close: "Got it",
						},
						step1: {
							title: "Link employees to points of sale",
							content:
								"Employees need to be linked to points of sale to use the machines and record sales. You can define whether everyone in the same role will work at the same points of sale or distribute them individually.",
						},
						step2: {
							title: "Distribute manually, if you prefer",
							content:
								"Enable the Distribute manually option to define where each employee will work. This way, you can choose which points of sale each employee will have access to and view this individually.",
						},
					},
				},
				review: {
					title: "<highlight>Employees and roles</highlight> configured!",
					subtitle: "The employees and roles for this operation are already configured.",
					subStepTitle: "Review",
					pendingTitle: "Step 4: <highlight>pending settings</highlight>",
					pendingSubtitle: "You decided to configure this step later.",
					reviewTitle: "Review your answers",
					reviewSubtitle: "You can edit them now, before completing the step.",
					editStep: "Edit step",
					reviewAlertTitle: "Review this step carefully.",
					reviewAlert:
						"Once you proceed, these settings can no longer be edited here and can only be adjusted directly in the Dashboard modules.",
					notConfigured: "Step not configured",
					pendingEmptySubtitle:
						"You have not configured the employees for this operation yet.",
				},
			},
		},
		operationTypes: {
			token: {
				label: "Token",
				description: "Customer exchanges token for product",
			},
			cashlessPrepaid: {
				label: "Cashless (prepaid)",
				description: "Customer loads credit on a card",
			},
			cashlessPostpaid: {
				label: "Cashless (postpaid)",
				description: "Customer pays on a card after consumption",
			},
			directSale: {
				label: "Direct sale at bar",
				description: "Customer pays for the product directly",
			},
			tables: {
				label: "Tables",
				description: "Customer places order with waiters",
			},
		},
		summary: {
			title: "Configuration summary for <placeName>{{placeName}}</placeName>!",
			subtitle: "Edit answers, go back steps or access advanced settings at any time.",
			editStep: "Edit step",
			operationType: "Operation type",
			functionalities: "Functionalities",
			viewFunctionalities: "View functionalities",
			advancedConfigurations: "Advanced Functionality Settings",
			notInformed: "Not informed",
			selectedCount: "{{count}} selected",
			pendingAction: "Pending action",
			loading: "Loading settings...",
			placeIdNotFound: "Place ID not found",
			errorLoading: "Error loading data",
			menuContent: {
				manageLink: "Advanced Menu Settings",
				empty: {
					title: "Nothing here yet",
					subTitle: "Configure this step to see the summary here.",
				},
				advancedConfig: "Advanced configuration",
			},
			employeesContent: {
				manageLink: "Advanced Teams and Roles Settings",
			},
			stepEmpty: {
				title: "Nothing here yet",
				subTitle:
					"The configuration wizard is no longer available, but you can complete this setup in the Dashboard.",
				buttonLabel: "Advanced configuration",
			},
		},
	},
	components: {
		pdvCard: {
			menu: {
				edit: "Edit",
				remove: "Remove",
				ariaLabel: "Options menu",
			},
			buttons: {
				cancel: "Cancel",
				save: "Save",
			},
		},
		productCard: {
			menu: {
				edit: "Edit",
				remove: "Remove",
				ariaLabel: "Options menu",
			},
		},
		positionCard: {
			menu: {
				viewPermissions: "View permissions",
				edit: "Edit",
				duplicate: "Duplicate",
				remove: "Remove",
				ariaLabel: "Options menu",
			},
			decrementLabel: "Decrease quantity",
			incrementLabel: "Increase quantity",
		},
		menuCard: {
			productsCount: "{{count}} products",
		},
		empty: {
			title: "No data found",
			description: "It seems there is no data to display.",
			button: "Add new item",
		},
		attachmentsFile: {
			loadingText: "Please wait while we send the file...",
			dragText: "Drag and drop the file",
			clickText: "or click to select",
			fileTypeText: "File type {{fileType}} up to {{fileSize}}.",
			maxSizeExceeded: "The file size exceeds the allowed limit.",
		},
		modalSelectImage: {
			productImage: "Edit image",
			setTagInput: "search for the product using keywords",
			notInformed: "Name not informed",
			confirm: "Confirm",
			selectFrom: "Select image from your ",
			computer: "computer",
			addImage: "Add image",
			typeOfFile: "File type .jpg, .jpeg, .png up to 5MB",
			errorCreatingImageUrl: "Error creating image URL",
		},
		employeePdvLink: {
			manualDistribution: "Distribute manually",
			description:
				"Choose for which points of sale all <strong>{{count}} {{roleName}}</strong> will work",
			selectedCount: "{{count}} Selected",
			selectPointsOfSale: "Select points of sale",
			noEmployees: "No employees created for this role.",
			noEmployeesMessage:
				"Set the number of people in this role to enable distribution by point of sale.",
		},
		employeeRoleTable: {
			columnOperator: "OPERATOR",
			columnLogin: "LOGIN",
			columnPassword: "PASSWORD",
			columnPdv: "LINKED POINTS OF SALE",
			linkedTo: "Linked to",
			employeesCount: "{{count}} employees",
			viewPermissions: "View permissions",
			notInformed: "Not informed",
			noEmployees: "No employees yet",
		},
		menuHeader: {
			linkedTo: "Linked to:",
			noLinkedPdv: "No point of sale linked",
			noLinkedMenu: "No menu linked",
			notConfigured: "Not configured",
			productsCount: "{{count}} PRODUCTS",
		},
	},
};
